// The Report section of a task (design §7): provenance bar, version picker,
// the sandboxed frame, Full screen / Download HTML, and the submissions.
// Presentational: data comes in via props, actions go out via callbacks.
// A report's `goblic.submit` opens the confirmation dialog here; only after
// "Send" does `onSubmit` (the API call) run.

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { EmptyState, ErrorNote, LoadingRows, Notice } from '../ui'
import { ReportFrame } from './report-frame'
import { ReportProvenance } from './report-provenance'
import { ReportSubmitDialog } from './report-submit-dialog'
import { ReportSubmissions } from './report-submissions'
import type {
  ReportLastSubmission,
  ReportRenderView,
  ReportSubmissionView,
  ReportSubmitResult,
  ReportVersionSummary,
  ReportView,
} from './report-types'
import { ReportVersionPicker } from './report-version-picker'

export interface ReportPanelProps {
  /** The task's report; `null` = none yet (empty state). */
  report: ReportView | null
  /** Rendered version being shown; `null` while loading. */
  render: ReportRenderView | null
  loading?: boolean
  error?: unknown
  versions: ReportVersionSummary[]
  /** Version being shown (defaults to `render.version`). */
  selectedVersion?: number
  onSelectVersion?: (version: number) => void
  submissions?: ReportSubmissionView[]
  /** `POST /reports/:id/submissions` with the shown version; resolve with the outcome. */
  onSubmit?: (data: unknown, version: number) => Promise<ReportSubmitResult>
  /** False when the user may not submit (`report.submit`). */
  canSubmit?: boolean
  /** The viewer's own latest submission, for the report's `goblic.getLastSubmission()`. */
  onLastSubmission?: () => Promise<ReportLastSubmission | null>
  /** Shown between the provenance bar and the frame (e.g. "new version available"). */
  notices?: ReactNode
  onFullScreen?: () => void
  onDownload?: () => void
  downloading?: boolean
  /** Full-screen route: no submissions list, a taller frame. */
  variant?: 'inline' | 'fullscreen'
  /** Extra actions (e.g. a link back to the task in full screen). */
  actions?: ReactNode
}

interface PendingSubmission {
  data: unknown
  version: number
  resolve: (result: ReportSubmitResult) => void
}

export function ReportPanel({
  report,
  render,
  loading,
  error,
  versions,
  selectedVersion,
  onSelectVersion,
  submissions = [],
  onSubmit,
  canSubmit = true,
  onLastSubmission,
  notices,
  onFullScreen,
  onDownload,
  downloading,
  variant = 'inline',
  actions,
}: ReportPanelProps) {
  const [pending, setPending] = useState<PendingSubmission | null>(null)
  const [sending, setSending] = useState(false)
  const [dialogError, setDialogError] = useState<string | null>(null)
  const [notice, setNotice] = useState<
    { tone: 'success' | 'error'; text: string } | null
  >(null)

  // Never leave a report's promise hanging if the panel goes away.
  const pendingRef = useRef<PendingSubmission | null>(null)
  pendingRef.current = pending
  useEffect(
    () => () =>
      pendingRef.current?.resolve({
        ok: false,
        error: 'The report was closed before sending.',
      }),
    []
  )

  const shownVersion = selectedVersion ?? render?.version
  const versionRef = useRef(shownVersion)
  versionRef.current = shownVersion

  const handleSubmitRequest = useCallback(
    (data: unknown): Promise<ReportSubmitResult> => {
      const version = versionRef.current
      if (!onSubmit || !canSubmit || version == null) {
        return Promise.resolve({
          ok: false,
          error: 'You can’t send data from this report.',
        })
      }
      setDialogError(null)
      return new Promise<ReportSubmitResult>((resolve) =>
        setPending({ data, version, resolve })
      )
    },
    [onSubmit, canSubmit]
  )

  // The frame navigated away and was reset: the dialog it opened no longer
  // belongs to anything on screen.
  const handleFrameReset = useCallback(() => {
    const open = pendingRef.current
    if (!open) return
    open.resolve({ ok: false, error: 'The report was reset.' })
    setPending(null)
    setDialogError(null)
  }, [])

  function cancel(): void {
    if (!pending || sending) return
    pending.resolve({
      ok: false,
      error: dialogError ?? 'Cancelled by the user.',
    })
    setPending(null)
  }

  async function send(): Promise<void> {
    if (!pending || !onSubmit || sending) return
    setSending(true)
    setDialogError(null)
    let result: ReportSubmitResult
    try {
      result = await onSubmit(pending.data, pending.version)
    } catch (err) {
      result = {
        ok: false,
        error: err instanceof Error ? err.message : String(err),
      }
    }
    setSending(false)
    if (result.ok) {
      pending.resolve(result)
      setPending(null)
      setNotice({ tone: 'success', text: 'Sent to Goblic.' })
    } else {
      // Keep the dialog open so the user sees why and can retry; Cancel
      // hands the last error back to the report.
      setDialogError(result.error)
    }
  }

  if (error) return <ErrorNote error={error} what="the report" />
  if (!report && loading) return <LoadingRows label="Loading report…" />
  if (!report) {
    return (
      <EmptyState title="No report yet" art={null}>
        Agents can create one with report_write.
      </EmptyState>
    )
  }

  const fullscreen = variant === 'fullscreen'

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate font-display text-md font-semibold text-ink">
            {render?.title ?? report.title}
          </h3>
          {report.summary ? (
            <p className="mt-0.5 text-sm text-ink-2">{report.summary}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {onSelectVersion && shownVersion != null ? (
            <ReportVersionPicker
              versions={versions}
              value={shownVersion}
              currentVersion={report.currentVersion}
              onChange={onSelectVersion}
              disabled={sending || pending != null}
            />
          ) : null}
          {onFullScreen && !fullscreen ? (
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={onFullScreen}
            >
              Full screen
            </button>
          ) : null}
          {onDownload ? (
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={onDownload}
              disabled={downloading}
            >
              {downloading ? 'Downloading…' : 'Download HTML'}
            </button>
          ) : null}
          {actions}
        </div>
      </div>

      {render ? (
        <ReportProvenance
          provenance={render.provenance}
          version={render.version}
          currentVersion={report.currentVersion}
        />
      ) : null}

      {notices}

      {notice ? (
        <Notice tone={notice.tone} onDismiss={() => setNotice(null)}>
          {notice.text}
        </Notice>
      ) : null}

      <div className="overflow-hidden rounded-panel border border-line-strong">
        {render ? (
          <ReportFrame
            html={render.html}
            title={`Report: ${render.title}`}
            hasScripts={render.hasScripts}
            consentKey={`${render.reportId}:${render.version}`}
            onSubmitRequest={handleSubmitRequest}
            onLastSubmissionRequest={onLastSubmission}
            onReset={handleFrameReset}
            minHeight={fullscreen ? 600 : 240}
            maxHeight={fullscreen ? 20000 : 1600}
            staticHeight={fullscreen ? 'max(600px, calc(100vh - 16rem))' : 640}
          />
        ) : (
          <LoadingRows label="Loading report…" />
        )}
      </div>

      {!fullscreen ? (
        <section className="pt-3">
          <h4 className="mb-2 font-display text-base font-semibold text-ink">
            Submissions
            {submissions.length > 0 ? (
              <span className="ml-2 rounded-full bg-sunken px-1.5 text-xs font-semibold text-ink-3">
                {submissions.length}
              </span>
            ) : null}
          </h4>
          <ReportSubmissions submissions={submissions} />
        </section>
      ) : null}

      <ReportSubmitDialog
        open={pending != null}
        data={pending?.data}
        reportTitle={render?.title ?? report.title}
        sending={sending}
        error={dialogError}
        onSend={() => void send()}
        onCancel={cancel}
      />
    </div>
  )
}
